import {Component, OnInit, Inject} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {saveAs} from 'file-saver';
import {finalize, take} from 'rxjs/operators';
import {FileDTO} from '../../../../clm/models/FileDTO';
import {InsuredPersonDTO} from '../../../../ent/models/InsuredPersonDTO';
import {BasicPersonDetailDTO} from '../../../../ent/models/person/BasicPersonDetailDTO';
import {InsuredPersonService} from '../../../../ent/services/insured-person.service';
import {detailsExpand} from '../../../animations';
import {NameValueCodeDTO} from '../../../models/NameValueCodeDTO';
import {FileUploadService} from '../../../services/file-upload.service';
import {MatIconType} from '../../../types/icon.enum';
import {FormControl, Validators} from '@angular/forms';
import {MatDatepicker} from '@angular/material/datepicker';
import {DateAdapter} from '@angular/material/core';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import * as moment from 'moment';
import { SharedService } from '../../../services/shared-service';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
  },
};

@Component({
  selector: 'app-document-overview',
  templateUrl: './document-overview.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ],
  animations: [detailsExpand],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
  ],
})

export class DocumentOverviewComponent implements OnInit {
  readonly DisplayIcon = MatIconType;
  options: NameValueCodeDTO[] = [
    {name: 'Certificate of Membership', value: null, code: 'COM'},
    {name: 'Membership Statement', value: null, code: 'STM'},
    {name: 'Tax Certificate', value: null, code: 'TAX'}
  ];

  isLoading = false;
  errorMessage: string;
  insuredPerson: InsuredPersonDTO;
  selectedDocument: NameValueCodeDTO;
  taxYearOptions: number[] = [];
  selectedTaxYear = '';
  dateForm: FormControl;
  maxDate: Date;
  private readonly _currentDate = new Date();
  readonly _maxYearMonth = new Date(this._currentDate.getFullYear(), this._currentDate.getMonth() - 1, 30);
  readonly _maxYear = new Date();

  constructor(
    private insuredPersonService: InsuredPersonService,
    private fileService: FileUploadService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetails: BasicPersonDetailDTO
  ) {
  }

  ngOnInit(): void {
    this.dateForm = new FormControl(null, [Validators.required]);
    if (this.basicPersonDetails.personId) {
      this.loadInsuredPerson();
    }
  }

  onDocumentTypeChanged(document: NameValueCodeDTO) {
    this.dateForm.reset();
    this.errorMessage = '';
    this.selectedDocument = document;
    this.selectedTaxYear = '';
  }

  downloadDocument(): void {
    if (!this.insuredPerson || !this.selectedDocument) {
      return;
    }

    const membershipNumber = this.insuredPerson?.policyNumber;
    const productCode = this.insuredPerson?.productCode;
    if (!membershipNumber || !productCode) {
      return;
    }
    this.isLoading = true;
    let documentFileCall$;

    switch (this.selectedDocument.code) {
    case 'TAX':
      documentFileCall$ = this.insuredPersonService.getTaxFile(membershipNumber, productCode, this.dateForm.value.format('YYYY'));
      break;
    case 'COM':
      documentFileCall$ = this.insuredPersonService.getCertificateOfMembership(membershipNumber, productCode);
      break;
    case 'STM':
      documentFileCall$ = this.insuredPersonService.getMemberStatement(membershipNumber, productCode, this.dateForm.value.format('YYYY-MM'));
      break;
    default:
      this.errorMessage = 'Unknown document type.';
      this.isLoading = false;
      return;
    }

    documentFileCall$
      .pipe(
        take(1),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (fileDTO: FileDTO) => {
          if (!fileDTO || !fileDTO.file) {
            return;
          }

          const blobData = this.fileService.base64toBlob(fileDTO.file, 'application/octet-stream');
          const blob = new Blob([blobData]);
          const {name, extension} = fileDTO;
          const fileName = `${name}${extension}`;
          saveAs(blob, fileName);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error?.businessMessage || 'Failed to generate document.');
        }
      });
  }

  setMonthAndYear(normalizedMonthAndYear: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    datepicker.close();
    const ctrlValue = moment();
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    this.dateForm.setValue(ctrlValue);
  }

  isDownloadButtonDisabled(): boolean {
    switch (this.selectedDocument?.code) {
    case 'COM' :
      return false;
    case 'TAX':
      return !this.validateDate();
    case 'STM':
      return !this.validateDate();
    default :
      return true;
    }
  }

  validateDate(): boolean {
    if (this.dateForm.value && this.dateForm.value.toString() != '') {
      return true;
    }
    return false;
  }


  private loadInsuredPerson(): void {
    this.isLoading = true;
    this.insuredPersonService.getInsuredPerson(this.basicPersonDetails.personId)
      .pipe(
        take(1),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response: InsuredPersonDTO) => {
          this.insuredPerson = response;
        },
        error: (_) => {
        }
      });
  }
}
